<script lang="ts">
import {defineComponent, ref, unref, onMounted, reactive} from 'vue'
import MoneyInput from '@/components/form/MoneyInput.vue'
import NewMoneyInput from '@/components/form/NewMoneyInput.vue'
import { formatMoney } from '../../utils/money'
import { useI18n } from 'vue-i18n'
import dinero from 'dinero.js'
import insuranceSvg from "@/assets/images/insurance.svg"
import {
  ClickOutside as vClickOutside,
  type ComponentSize,
  type FormInstance, type FormRules
} from 'element-plus'
import Insurance from "@/models/insurance/Insurance";
import {useHTTP} from "@/plugins/http";
import {Order} from "@/models/sales/Order";
import { useSettingsStore } from '@/stores/settingsStore'

/**
 * UserInput:
 *
 * - paidInEpaymentDefaultCurrency
 * - paidBeforeChangeInDefaultCurrency
 * - paidInEpaymentSecondaryCurrency
 * - paidBeforeChangeInSecondaryCurrency
 * - exchangeRate
 *
 * -------------------------------------------------------------------------------------------------
 *
 * Calculated and passed to the component:
 * - TotalDue
 *
 * -------------------------------------------------------------------------------------------------
 *
 * Calculated and autocorrected by this component
 * - TotalPaid (in default currency)
 * - changeDue (in default currency)
 * - totalPaidInEpayment
 * - totalPaidInCash
 * - paidInEpaymentDefaultCurrency (autocorrected)
 * - paidInEpaymentSecondaryCurrency
 * - paidInCashDefaultCurrency (autocorrected)
 * - paidInCashSecondaryCurrency
 *
 * -------------------------------------------------------------------------------------------------
 * Example: a user pays IQD 100,000 in cash, 200,000 IQD in IQD Epayment, $100 in USD cash, 200 in USD Epayment.
 * Exchange rate: 1 USD = 1300 IQD
 * Total due = 700,000 IQD
 *
 * First, use customer credit (highest priority)
 * (assuming customer has 50,000 IQD credit available)
 * 700,000 IQD - 50,000 IQD = 650,000 IQD
 *
 * then use the e-payment in IQD
 * 650,000 IQD - 200,000 IQD = 450,000 IQD
 * then use the e-payment in USD
 * 450,000 IQD - ($200 * 1300) = 190,000 IQD
 *
 * then use the cash in IQD
 * 190,000 IQD - 100,000 = 90,000 IQD
 * then use the cash in USD
 * 90,000 - $100 * 1300 = 90,000 - 130,000 = 0 IQD (with 40,000 IQD change)
 *
 * -------------------------------------------------------------------------------------------------
 *                                       TotalDue
 *                                          -
 *   totalPaidInEpayment = [paidInEpaymentDefaultCurrency + convert(paidInEpaymentSecondaryCurrency)]
 *                                          -
 *   totalPaidInCash = [paidInCashDefaultCurrency + convert(paidInCashSecondaryCurrency)]
 *                                          =
 *                       ChangeDue (in default currency, if any change)
 *  ------------------------------------------------------------------------------------------------
 *            totalPaid = min(totalDue, totalPaidInEpayment + totalPaidInCash)
 *  ------------------------------------------------------------------------------------------------
 */
interface InsuranceForm {
  insurance?: string
  amountToBePaid?: number
}

export default defineComponent({
  name: 'MultiCurrencyPaymentInput',

  components: { NewMoneyInput, MoneyInput },

  setup(props, {emit}) {
    const { t } = useI18n({
      messages: {
        en: {
          total: 'Total Due',
          cash: 'Amount Paid',
          changeDue: 'Change Due',
          totalPaid: 'Total received',
          iqdCashReceived: 'Cash Received in IQD',
          iqdEpaymentReceived: 'E-payment Received in IQD',
          usdCashReceived: 'Cash Received in USD',
          usdEpaymentReceived: 'E-payment Received in USD',
          exchangeRate: 'exchangeRate',
          payFull: 'Full',
          insuranceCompany: 'Agency',
          insuranceCompanyPlaceholder: 'Agency name',
          amountToBePaid: 'To pay',
          clearAmount: 'Clear',
          insurancePay: 'Insurance Pay',
          customerPay: 'Customer Pay',
          totalPaidLabel: 'Total paid',
          iqdCashPaid: 'Cash Paid in IQD',
          iqdEpaymentPaid: 'E-payment Paid in IQD',
          usdCashPaid: 'Cash Paid in USD',
          usdEpaymentPaid: 'E-payment Paid in USD',
          iqdCreditUsed: 'IQD Credit Used',
          availableCredit: 'Available Credit',
          useAllCredit: 'Use All',
          terminalRequired: 'A terminal must be selected when using E-Payment'
        },
        ar: {
          total: 'المبلغ المطلوب',
          cash: 'المبلغ المدفوع',
          changeDue: 'المبلغ الباقي',
          totalPaid: 'المبلغ المستحصل',
          iqdCashReceived: 'المدفوع نقدا بالدينار',
          iqdEpaymentReceived: 'المدفوع الكترونيا بالدينار',
          usdCashReceived: 'المدفوع نقدا بالدولار',
          usdEpaymentReceived: 'المدفوع الكترونيا بالدولار',
          exchangeRate: 'سعر التصريف',
          payFull: 'دفع كامل',
          insuranceCompany: 'شركة التأمين',
          insuranceCompanyPlaceholder: 'شركة التأمين',
          amountToBePaid: 'تدفع مبلغ',
          clearAmount: 'تفريغ',
          insurancePay: 'يدفع الضامن',
          customerPay: 'يدفع الزبون',
          totalPaidLabel: 'المبلغ المدفوع',
          iqdCashPaid: 'المدفوع نقدا بالدينار',
          iqdEpaymentPaid: 'المدفوع الكترونيا بالدينار',
          usdCashPaid: 'المدفوع نقدا بالدولار',
          usdEpaymentPaid: 'المدفوع الكترونيا بالدولار',
          iqdCreditUsed: 'الرصيد المستخدم بالدينار',
          availableCredit: 'الرصيد المتاح',
          useAllCredit: 'استخدم كل الرصيد',
          terminalRequired: 'يجب اختيار جهاز عند استخدام الدفع الإلكتروني'
        }
      }
    })

    const http = useHTTP();
    const insurances = ref<Array<Insurance>>([])
    const insuranceDialogVisiable = ref(false)
    const formSize = ref<ComponentSize>('default')
    const insuranceFormRef = ref<FormInstance>()
    const insuranceForm = ref<InsuranceForm>({
      insurance: undefined,
      amountToBePaid: 0,
    })


    const insuranceFormRules = reactive<FormRules<InsuranceForm>>({
      insurance: [
        {
          required: true,
          message: t('validation.required'),
          trigger: 'blur'
        },
      ],
      amountToBePaid: [
        {
          required: true,
          trigger: 'change',
        },
      ],
    })

    onMounted(async () => {
        try {
          insuranceForm.value.insurance = props.order?.invoice.insuranceID;
          insuranceForm.value.amountToBePaid = props.order?.invoice.insuranceTotalDueAmount;

          const insurancesResponse = await http.get<Array<Insurance>>(Insurance.ENDPOINT)
          insurances.value = insurancesResponse.map((insurance) => Insurance.from(insurance));
        } catch (e) {
          console.log(e)
        }
    })

    const submitForm = async (formEl: FormInstance | undefined) => {
      if (!formEl) return
      await formEl.validate((valid, fields) => {
        if (valid) {
          emit("insurance", insuranceForm.value)
          insuranceDialogVisiable.value = false;
          console.log(insuranceForm.value)
        } else {
          console.log('error submit!', fields)
        }
      })
    }


    function clear() {
      insuranceForm.value = {
        insurance: undefined,
        amountToBePaid: 0,
      }
      emit("insurance", undefined);
      insuranceDialogVisiable.value = false;
    }


    return {
      t,
      insuranceSvg,
      insuranceDialogVisiable,
      clear,
      submitForm,
      formSize,
      insuranceFormRef,
      insuranceForm,
      insurances,
      insuranceFormRules,
      useSettingsStore
    }
  },

  // What other components will provide
  props: {
    // IN DEFAULT CURRENCY
    totalDue: {
      type: Number,
      required: true
    },

    // In Default Currency
    totalPaidInEpayment: {
      type: Number,
      required: true
    },

    // In Default Currency
    totalPaidInCashBeforeChange: {
      type: Number,
      required: true
    },

    // In Default Currency
    totalPaidInCredit: {
      type: Number,
      required: false
    },

    // In Default Currency
    changeDue: {
      type: Number,
      required: true
    },

    // totalPaidInEpayment + totalPaidInCash (after change)
    totalPaid: {
      type: Number,
      required: true
    },

    // produced and calculated by this component
    cashMultiCurrencyBreakdown: {
      type: Array
    },

    ePaymentMultiCurrencyBreakdown: {
      type: Array
    },

    iqdCashOnly: {
      type: Boolean,
      value: false,
    },

    order: {
      type: Order,
      required: false
    },

    usePaidLabel: {
      type: Boolean,
      value: false,
    },

    customerID: {
      type: String,
      required: false
    },

    hideCurrency: {
      type: Boolean,
      value: false,
    },

    availableTerminals: {
      type: Array,
      default: () => []
    },

    terminalError: {
      type: Boolean,
      default: false
    },
  },

  // state:
  data() {

    const defaultCurrency = this.$ctx.currency;

    return {
      defaultCurrency,

      secondaryCurrency: defaultCurrency === 'IQD' ? 'USD' : 'IQD',

      selectedTerminalID: '' as string,
      selectedLastFourDigits: '' as string,

      // IQD
      paidInCashIQD: 0,
      paidInCreditIQD: 0,
      paidInEpaymentIQD: 0,

      // USD
      paidInCashUSD: 0,
      paidInEpaymentUSD: 0,

      IQDToUSDExchangeRate: this.$ctx.exchangeRates.USD,
      USDToIQDExchangeRate: this.$ctx.exchangeRates.IQD,

      // Total
      totalCashAndEpaymentBeforeChange: 0,

      // tabs
      selectedTabCurrency: defaultCurrency,
      tabs: defaultCurrency === 'IQD' ? ['IQD', 'USD', 'Credit'] : ['USD', 'IQD', 'Credit'],

      // Customer Credit
      customerCredit: 0,
    }
  },

  // change values anytime user input changes
  watch: {
    paidInCashIQD() {
      this.updateTotal()
    },

    paidInCreditIQD() {
      this.updateTotal()
    },

    paidInEpaymentIQD() {
      this.updateTotal()
    },

    paidInCashUSD() {
      this.updateTotal()
    },

    paidInEpaymentUSD() {
      this.updateTotal()
    },

    IQDToUSDExchangeRate() {
      this.updateTotal()
    },

    USDToIQDExchangeRate() {
      this.updateTotal()
    },
    customerID() {
      this.getCustomerCredit()
      // If customerID is removed and we're on Credit tab, switch to default currency tab
      if (!this.customerID && this.selectedTabCurrency === 'Credit') {
        this.selectedTabCurrency = this.defaultCurrency
      }
      // Clear credit amount when customer is removed
      if (!this.customerID) {
        this.paidInCreditIQD = 0
        this.updateTotal();
      }
    },
    customerCredit() {
      // If customer credit becomes zero or negative and we're on Credit tab, switch to default currency tab
      if (this.customerCredit <= 0 && this.selectedTabCurrency === 'Credit') {
        this.selectedTabCurrency = this.defaultCurrency
      }
      // Clear credit amount when customer credit becomes zero or negative
      if (this.customerCredit <= 0) {
        this.paidInCreditIQD = 0
      }
    }
  },

  // provide updated values to the caller component

  // methods
  methods: {
    formatMoney,
    getCustomerCredit() {
      if (!this.customerID) {
        return;
      }

      this.$http.get<number>(Order.ENDPOINT + `/customer-credit/${this.customerID}`).then((res) => {
        this.customerCredit = res
      })
    },
    updateTotal() {
      // values given to component that we need in doing the math below
      const totalDue = this.totalDue;
      const exchangeRate = this.defaultCurrency === "IQD" ? this.USDToIQDExchangeRate : this.IQDToUSDExchangeRate;
      const defaultCurrencyPrecision = this.defaultCurrency === "IQD" ? 0 : 2;
      const secondaryCurrencyPrecision = this.secondaryCurrency === "IQD" ? 0 : 2;

      // first define which is default currency and which is secondary
      // User input
      const paidInCashDefaultCurrencyBeforeChange = this.defaultCurrency === "IQD" ? this.paidInCashIQD : this.paidInCashUSD / 100;
      const paidInCashSecondaryCurrencyBeforeChange = this.secondaryCurrency === "IQD" ? this.paidInCashIQD : this.paidInCashUSD / 100;
      let paidInCreditDefaultCurrencyBeforeChange = this.defaultCurrency === "IQD" ? this.paidInCreditIQD : 0;

      const totalPaidInCashBeforeChangeConverted = paidInCashDefaultCurrencyBeforeChange + Math.round(paidInCashSecondaryCurrencyBeforeChange * exchangeRate)

      // User input before autocorrection
      let paidInEpaymentDefaultCurrency = this.defaultCurrency === "IQD" ? this.paidInEpaymentIQD : this.paidInEpaymentUSD / 100;

      // The algorithm is simple
      // first deduct credit (highest priority)
      // then deduct default currency epayment
      // then deduct cash of default currency
      // then deduct foreign currency

      // we start by autocorrecting credit first (highest priority)
      let remainingBalance = totalDue;

      paidInCreditDefaultCurrencyBeforeChange = Math.min(paidInCreditDefaultCurrencyBeforeChange, remainingBalance)
      this.paidInCreditIQD = paidInCreditDefaultCurrencyBeforeChange
      remainingBalance -= paidInCreditDefaultCurrencyBeforeChange;

      // then deduct epayment
      paidInEpaymentDefaultCurrency = Math.min(paidInEpaymentDefaultCurrency, remainingBalance)
      remainingBalance -= paidInEpaymentDefaultCurrency

      // update user input to match the autocorrected values
      if (this.defaultCurrency === "IQD") {
        this.paidInEpaymentIQD = paidInEpaymentDefaultCurrency
      } else {
        this.paidInEpaymentUSD = paidInEpaymentDefaultCurrency
      }

      // finally, deduct cash
      const totalPaidInPrimaryCurrency = Math.min(paidInCashDefaultCurrencyBeforeChange, remainingBalance);
      remainingBalance -= totalPaidInPrimaryCurrency;

      // then secondary currency
      const paidInSecondaryCurrencyBeforeChangeConverted = Math.round(paidInCashSecondaryCurrencyBeforeChange * exchangeRate);
      const totalPaidInSecondaryCurrencyConverted = Math.min(paidInSecondaryCurrencyBeforeChangeConverted, remainingBalance);
      const totalChangeDueAfterSecondaryCurrencyConverted =  paidInSecondaryCurrencyBeforeChangeConverted - totalPaidInSecondaryCurrencyConverted;

      // now we need to make sure that all the numbers are accurate
      // convert it back (to USD for example);
      let totalPaidInSecondaryCurrency = Math.round(totalPaidInSecondaryCurrencyConverted / exchangeRate * 100) / 100;
      const totalChangeDueAfterSecondaryCurrency = Math.round(totalChangeDueAfterSecondaryCurrencyConverted / exchangeRate * 100) / 100;
      // this could be a cent for example. something like: $400 = $385.61 + $15.38
      // so we fix this to make sure totalPaid + change = receivedCash
      const currencyConversionRoundingError = paidInCashSecondaryCurrencyBeforeChange - totalPaidInSecondaryCurrency - totalChangeDueAfterSecondaryCurrency;
      if (currencyConversionRoundingError) {
        totalPaidInSecondaryCurrency +=  currencyConversionRoundingError;
      }

      // now we make sure there are no rounding errors per total paid and change
      // ideally, if we convert back totalPaidInSecondaryCurrency, it should equal totalPaidInSecondaryCurrencyConverted
      const totalPaidSecondaryRoundingError = totalPaidInSecondaryCurrencyConverted - Math.round(totalPaidInSecondaryCurrency * exchangeRate);
      const totalChangeDueAfterSecondaryRoundingError = totalChangeDueAfterSecondaryCurrencyConverted - Math.round(totalChangeDueAfterSecondaryCurrency * exchangeRate);

      const totalPaidInCash = totalPaidInPrimaryCurrency + totalPaidInSecondaryCurrencyConverted;

      // values we need to update every time user input changes
      const newTotalPaidInCashBeforeChange = totalPaidInCashBeforeChangeConverted
      const newTotalPaidInEpayment = paidInEpaymentDefaultCurrency;
      const newTotalPaid = totalPaidInCash + newTotalPaidInEpayment + paidInCreditDefaultCurrencyBeforeChange;

      const newChangeDue = totalPaidInCashBeforeChangeConverted - totalPaidInCash
      // According to JS
      // 303.85 * 100  = 30385.000000000004
      // so the round function below is to ensure we're left with an integer...
      // it sucks... but DON'T YOU DARE removing it.
      const newMultiCurrencyBreakdownForCash: Array<{}> = [
        {
          currency: this.defaultCurrency,
          precision: defaultCurrencyPrecision,
          amountBeforeChange: Math.round(paidInCashDefaultCurrencyBeforeChange * (Math.pow(10,  defaultCurrencyPrecision))),
          amount: Math.round(totalPaidInPrimaryCurrency * Math.pow(10, defaultCurrencyPrecision))
        },
        {
          currency: this.secondaryCurrency,
          precision: secondaryCurrencyPrecision,
          amountBeforeChange: Math.round(paidInCashSecondaryCurrencyBeforeChange * (Math.pow(10, secondaryCurrencyPrecision))),
          amount: Math.round(totalPaidInSecondaryCurrency * Math.pow(10, secondaryCurrencyPrecision)),
          amountConversionError: totalPaidSecondaryRoundingError,
          exchangeRate: exchangeRate,
          changeDue: Math.round(totalChangeDueAfterSecondaryCurrency * Math.pow(10, secondaryCurrencyPrecision)),
          changeDueConversionError: totalChangeDueAfterSecondaryRoundingError
        }
      ]

      // update the comprehensive total, just for display in this component
      this.totalCashAndEpaymentBeforeChange = totalPaidInCashBeforeChangeConverted + newTotalPaidInEpayment + paidInCreditDefaultCurrencyBeforeChange

      // after doing all the calculations, do the update
      this.$emit('update:totalPaidInCashBeforeChange', newTotalPaidInCashBeforeChange)
      this.$emit('update:totalPaidInCredit', paidInCreditDefaultCurrencyBeforeChange)
      this.$emit('update:totalPaidInEpayment', newTotalPaidInEpayment)
      // newTotalPaidInCash + newTotalPaidInEpayment
      this.$emit('update:totalPaid', newTotalPaid)
      // change = max(totalDue - totalPaid, 0)
      this.$emit('update:changeDue', newChangeDue)
      this.$emit('update:cashMultiCurrencyBreakdown', newMultiCurrencyBreakdownForCash)
    },

    addToPaidInCashIQD(val: number) {
      this.paidInCashIQD += val
    },

    addToPaidInCashUSD(val: number) {
      this.paidInCashUSD += val
    },

    cashReceivedInputFocus() {
      this.$nextTick(() => {
        /* @ts-ignore */
        const cashReceivedInput = this.defaultCurrency === "IQD" ? this.$refs['iqdCashInput'] : this.$refs['usdCashInput'];

        if (cashReceivedInput) {
          /* @ts-ignore */
          cashReceivedInput.focus()
        }
      })
    },
  },

  mounted() {
    // Initialize internal cash values from the breakdown if it exists
    if (this.cashMultiCurrencyBreakdown && Array.isArray(this.cashMultiCurrencyBreakdown)) {
      for (const breakdown of this.cashMultiCurrencyBreakdown) {
        const currencyBreakdown = breakdown as any
        if (currencyBreakdown.currency === 'IQD') {
          this.paidInCashIQD = currencyBreakdown.amountBeforeChange || 0
        } else if (currencyBreakdown.currency === 'USD') {
          this.paidInCashUSD = currencyBreakdown.amountBeforeChange || 0
        }
      }
    }

    // Initialize e-payment values from totalPaidInEpayment
    if (this.totalPaidInEpayment > 0) {
      if (this.defaultCurrency === 'IQD') {
        this.paidInEpaymentIQD = this.totalPaidInEpayment
      } else {
        this.paidInEpaymentUSD = this.totalPaidInEpayment
      }
    }

    // Initialize credit values from totalPaidInCredit
    if (this.totalPaidInCredit && this.totalPaidInCredit > 0) {
      if (this.defaultCurrency === 'IQD') {
        this.paidInCreditIQD = this.totalPaidInCredit
      }
    }

    this.getCustomerCredit();

    const interval = setInterval(() => {
      const cashReceivedInput = this.defaultCurrency === "IQD" ? this.$refs['iqdCashInput'] : this.$refs['usdCashInput'];
      if (cashReceivedInput) {
        (cashReceivedInput as any).focus()
        clearInterval(interval)
      }
    }, 50)
  }
})
</script>

<template>
  <!-- default currency appears first -->
  <el-dialog
      v-model="insuranceDialogVisiable"
      width="400"
  >
    <el-form
        ref="insuranceFormRef"
        :rules="insuranceFormRules"
        :model="insuranceForm"
        label-width="auto"
        :size="formSize"
        status-icon
        class="!block mt-2"
    >
      <el-form-item :label="t('insuranceCompany')" prop="insurance" required>
        <el-select filterable clearable v-model="insuranceForm.insurance"
                   :placeholder="t('insuranceCompanyPlaceholder')">
          <el-option v-for="insurance in insurances" :key="insurance.id"
                     :label="insurance.name" :value="insurance.id"/>
        </el-select>
      </el-form-item>

      <el-form-item :label="t('amountToBePaid')" prop="amountToBePaid">
        <div class="flex gap-1">
          <money-input v-model="insuranceForm.amountToBePaid" currency="IQD"/>
          <el-button type="primary" @click="() => {
                    insuranceForm.amountToBePaid = totalDue
                  }">{{t('payFull')}}</el-button>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button type="primary" @click="submitForm(insuranceFormRef)">
          {{ t('confirm') }}
        </el-button>
        <el-button @click="insuranceDialogVisiable = false">{{ t('close') }}</el-button>
        <el-button @click="clear()">{{ t('clearAmount') }}</el-button>
      </div>
    </template>
  </el-dialog>
  <div>
    <div class="w-full flex flex-col gap-2">
      <slot name="before-total-due" />
      <div class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
        <div class="">{{t("total")}}</div>
        <div class="flex grow justify-end">
          <div dir="ltr" class="bg-yellow-100 w-1/2 p-1"> {{formatMoney(totalDue + (order?.invoice.insuranceTotalDueAmount || 0), "IQD")}}</div>
          <template v-if="useSettingsStore().getValue('show_insurance_button_on_sales_page') == 'true'">
            <div v-if="order" class="p-1 bg-gray-100 cursor-pointer" :class="{'!bg-blue-400': order?.invoice.insuranceID}" @click="insuranceDialogVisiable = true"><img :src="insuranceSvg" class="h-[24px]" alt=""></div>
          </template>
        </div>
      </div>
      <div v-if="order?.invoice.insuranceID" class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
        <div class="">{{t("insurancePay")}}</div>
        <div class="flex grow justify-end">
          <div dir="ltr" class="w-[165px] p-1"> {{formatMoney(order?.invoice.insuranceTotalDueAmount, "IQD")}}</div>
        </div>
      </div>
      <div v-if="order?.invoice.insuranceID" class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
        <div class="">{{t("customerPay")}}</div>
        <div class="flex grow justify-end">
          <div dir="ltr" class="bg-yellow-100/40 w-[165px] p-1"> {{formatMoney(totalDue, "IQD")}}</div>
        </div>
      </div>
    </div>

    <div class="w-full p-2">
      <hr class="solid" />
    </div>

    <!-- tab header -->
    <div class="flex w-full mb-2">
      <template v-for="tab in tabs">
        <div
            v-if="!iqdCashOnly && (tab !== 'Credit' || (customerID && customerCredit > 0))"
            :key="tab"
            class="w-1/2 py-2 text-center cursor-pointer border-blue-300 hover:border-b-4 mx-5 font-bold"
            :class="{'border-b-4': selectedTabCurrency === tab}"
            @click="selectedTabCurrency = tab"
        >
          {{ tab }}
        </div>
      </template>
    </div>

    <div class="flex justify-center flex-col items-center w-full">

      <!-- tab body -->
      <div v-if="selectedTabCurrency === 'IQD'" class="mx-5" :class="{'w-full': hideCurrency}">
        <div class="flex justify-center">
          <div v-if="!hideCurrency" id="Iqd-currency" class="w-full">
            <div class="flex justify-between">
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/250.webp"
                alt="250 iqd"
                @click="addToPaidInCashIQD(250)"
              />
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/500.webp"
                alt="500 iqd"
                @click="addToPaidInCashIQD(500)"
              />
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/1000.webp"
                alt="1000 iqd"
                @click="addToPaidInCashIQD(1000)"
              />

              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/5000.webp"
                alt="5000 iqd"
                @click="addToPaidInCashIQD(5000)"
              />
            </div>

            <div class="flex justify-between mt-4">
              <img
                class="w-4/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/10.webp"
                alt="10000 iqd"
                @click="addToPaidInCashIQD(10000)"
              />
              <img
                class="w-4/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/25.webp"
                alt="25000 iqd"
                @click="addToPaidInCashIQD(25000)"
              />
              <img
                class="w-4/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/50.webp"
                alt="50000 iqd"
                @click="addToPaidInCashIQD(50000)"
              />
            </div>

          </div>
        </div>

        <div class="flex justify-between items-center my-3 mt-6 text-primary w-full">
          <h1 class="2xl:text-l lg:text-l text-m">
            {{defaultCurrency == 'IQD' ? '(a)' : '(c)'}} {{ t(usePaidLabel ? "iqdCashPaid" : "iqdCashReceived") }}
          </h1>
          <div class="line border w-1/12 mx-2"></div>
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ formatMoney(paidInCashIQD, 'IQD') }}
          </h1>
        </div>

        <div :dir="$ctx.getDir()" class="w-full">
          <div class="w-full mb-5">
            <div class="flex items-center gap-2">
              <money-input
                v-model="paidInCashIQD"
                currency="IQD"
                ref="iqdCashInput"
              />
            </div>
          </div>
        </div>

        <div class="flex justify-between items-center my-3 text-primary w-full" v-if="defaultCurrency === 'IQD' && !iqdCashOnly">
          <h1 class="2xl:text-l lg:text-l text-m">
            {{defaultCurrency == 'IQD' ? '(b)' : '(d)'}} {{ t(usePaidLabel ? "iqdEpaymentPaid" : "iqdEpaymentReceived") }}
          </h1>
          <div class="line border w-1/12 mx-2"></div>
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ formatMoney(paidInEpaymentIQD, 'IQD') }}
          </h1>
        </div>

        <div class="w-full mb-5" v-if="defaultCurrency === 'IQD' && !iqdCashOnly">
          <div class="flex items-center gap-2">
            <money-input
              v-model="paidInEpaymentIQD"
              currency="IQD"
              ref="iqdEpaymentInput"
            />
            <el-select
              v-if="availableTerminals && availableTerminals.length > 0"
              :model-value="selectedTerminalID"
              placeholder="Terminal"
              class="w-36"
              :style="terminalError && !selectedTerminalID ? 'outline: 1px solid #f56c6c; border-radius: 4px;' : ''"
              @change="(val: string) => { selectedTerminalID = val; $emit('update:terminalID', val) }"
            >
              <el-option
                v-for="rt in availableTerminals"
                :key="rt.terminalID"
                :label="rt.terminal ? rt.terminal.enName : rt.terminalID"
                :value="rt.terminalID"
              />
            </el-select>
            <el-input
              v-if="availableTerminals && availableTerminals.length > 0"
              :model-value="selectedLastFourDigits"
              placeholder="Last 4 digits"
              maxlength="4"
              class="w-28"
              @input="(val: string) => { selectedLastFourDigits = val; $emit('update:lastFourDigits', val) }"
            />
          </div>
          <p v-if="terminalError && !selectedTerminalID && availableTerminals && availableTerminals.length > 0" class="text-red-500 text-xs mt-1">
            {{ t('terminalRequired') }}
          </p>
        </div>


        <template v-if="secondaryCurrency === 'IQD'">
          <div class="flex justify-between items-center my-3 text-primary w-full">
            <h1 class="2xl:text-l lg:text-l text-m">
              {{ t('exchangeRate') }}
            </h1>
            <div class="line border w-1/12 mx-2"></div>
            <h1 class="2xl:text-l lg:text-l text-m" dir="ltr">
              1 IQD = {{ IQDToUSDExchangeRate }} USD
            </h1>
          </div>

          <div class="w-full mb-5">
            <div class="flex items-center gap-2">
              <el-input
                v-model="IQDToUSDExchangeRate"
                ref="iqdToUsdInput"
              />
            </div>
          </div>
        </template>
      </div>

      <div v-if="selectedTabCurrency === 'USD' && !iqdCashOnly" class="mx-5">
        <div class="flex justify-center">

          <div id="Iqd-currency" class="w-full">
            <div class="flex justify-between">
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/1usd.webp"
                alt="1 USD"
                @click="addToPaidInCashUSD(100)"
              />
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/2usd.webp"
                alt="2 USD"
                @click="addToPaidInCashUSD(200)"
              />
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/5usd.webp"
                alt="5 USD"
                @click="addToPaidInCashUSD(500)"
              />
              <img
                class="w-3/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/10usd.webp"
                alt="10 USD"
                @click="addToPaidInCashUSD(1000)"
              />
            </div>

            <div class="flex justify-between mt-4">
              <img
                class="w-4/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/20usd.webp"
                alt="20 USD"
                @click="addToPaidInCashUSD(2000)"
              />
              <img
                class="w-4/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/50usd.webp"
                alt="50 USD"
                @click="addToPaidInCashUSD(5000)"
              />
              <img
                class="w-4/12 cursor-pointer px-2 transition ease-in-out delay-100 hover:scale-110 duration-100"
                src="@/assets/paperMoney/100usd.webp"
                alt="100 USD"
                @click="addToPaidInCashUSD(10000)"
              />
            </div>

          </div>
        </div>

        <div class="flex justify-between items-center my-3 mt-6 text-primary w-full">
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ defaultCurrency === 'USD' ? '(a)' : '(c)'}} {{ t(usePaidLabel ? "usdCashPaid" : "usdCashReceived") }}
          </h1>
          <div class="line border w-1/12 mx-2"></div>
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ formatMoney(paidInCashUSD, 'USD') }}
          </h1>


        </div>

        <div :dir="$ctx.getDir()" class="w-full">
          <div class="w-full mb-5">
            <div class="flex items-center gap-2">
              <new-money-input
                v-model="paidInCashUSD"
                currency="USD"
                ref="usdCashInput"
              />
            </div>
          </div>

          <template v-if="secondaryCurrency === 'USD'">
            <div class="flex flex justify-between items-center my-3 text-primary w-full">
              <h1 class="2xl:text-l lg:text-l text-m">
                {{ t('exchangeRate') }}
              </h1>
              <div class="line border w-1/12 mx-2"></div>
              <h1 class="2xl:text-l lg:text-l text-m" dir="ltr">
                1 USD = {{ USDToIQDExchangeRate }} IQD
              </h1>
            </div>

            <div class="w-full">
              <div class="flex items-center gap-2">
                <el-input
                  v-model="USDToIQDExchangeRate"
                  ref="usdToIqdInput"
                />
              </div>
            </div>
          </template>
        </div>

        <div class="flex justify-between items-center my-3 text-primary w-full" v-if="defaultCurrency=== 'USD'">
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ defaultCurrency === 'USD' ? '(b)' : '(d)'}} {{ t(usePaidLabel ? "usdEpaymentPaid" : "usdEpaymentReceived") }}
          </h1>
          <div class="line border w-1/12 mx-2"></div>
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ formatMoney(paidInEpaymentUSD, 'USD') }}
          </h1>
        </div>

        <div class="w-full" v-if="defaultCurrency === 'USD'">
          <!-- I chose to keep the below as a comment just in case we decided to use labels later on -->
          <!-- <p class="mb-2 font-semibold">{{ t("cash") }}</p> -->
          <div class="flex items-center gap-2">
            <money-input
              v-model="paidInEpaymentUSD"
              currency="USD"
              ref="usdEpaymentInput"
            />
            <el-select
              v-if="availableTerminals && availableTerminals.length > 0"
              :model-value="selectedTerminalID"
              placeholder="Terminal"
              class="w-36"
              :style="terminalError && !selectedTerminalID ? 'outline: 1px solid #f56c6c; border-radius: 4px;' : ''"
              @change="(val: string) => { selectedTerminalID = val; $emit('update:terminalID', val) }"
            >
              <el-option
                v-for="rt in availableTerminals"
                :key="rt.terminalID"
                :label="rt.terminal ? rt.terminal.enName : rt.terminalID"
                :value="rt.terminalID"
              />
            </el-select>
            <el-input
              v-if="availableTerminals && availableTerminals.length > 0"
              :model-value="selectedLastFourDigits"
              placeholder="Last 4 digits"
              maxlength="4"
              class="w-28"
              @input="(val: string) => { selectedLastFourDigits = val; $emit('update:lastFourDigits', val) }"
            />
          </div>
          <p v-if="terminalError && !selectedTerminalID && availableTerminals && availableTerminals.length > 0" class="text-red-500 text-xs mt-1">
            {{ t('terminalRequired') }}
          </p>
        </div>
      </div>

      <div v-if="selectedTabCurrency === 'Credit'" class="px-5 w-full">

        <div class="flex justify-between items-center my-3  w-full">
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ t('availableCredit') }}
          </h1>
          <h1 class="2xl:text-l lg:text-l text-m bg-cyan-50 !p-2" :class="{'!text-red-600 !bg-red-100': customerCredit < 0}">
            {{ formatMoney(customerCredit, 'IQD') }}
          </h1>
          <el-button type="primary" @click="() => {
            paidInCreditIQD = customerCredit > 0 ? customerCredit : 0
          }">
            {{ t('useAllCredit') }}
          </el-button>
        </div>

        <div class="flex justify-between items-center my-3 text-primary w-full">
          <h1 class="2xl:text-l lg:text-l text-m">
            {{defaultCurrency == 'IQD' ? '(d)' : '(d)'}} {{ t(usePaidLabel ? "iqdCreditUsed" : "iqdCreditUsed") }}
          </h1>
          <div class="line border w-1/12 mx-2"></div>
          <h1 class="2xl:text-l lg:text-l text-m">
            {{ formatMoney(paidInCreditIQD, 'IQD') }}
          </h1>
        </div>

        <div :dir="$ctx.getDir()" class="w-full">
          <div class="w-full mb-5">
            <div class="flex items-center gap-2">
              <money-input
                v-model="paidInCreditIQD"
                currency="IQD"
                ref="iqdCreditInput"
              />
            </div>
          </div>
        </div>
      </div>


      <div id="totals" v-if="defaultCurrency === 'IQD'" class="w-full text-sm mt-1" dir="ltr">

        <div v-if="!iqdCashOnly" class="w-full p-2">
          <hr class="solid" />
        </div>

        <div v-if="!iqdCashOnly" class="mx-5 flex mt-1">
          <div dir="ltr"> &nbsp &nbsp(a) {{formatMoney(paidInCashIQD, "IQD")}}</div>
        </div>
        <div v-if="!iqdCashOnly" class="mx-5 flex">
          <div dir="ltr"> + (b) {{formatMoney(paidInEpaymentIQD, "IQD")}}</div>
        </div>
        <div v-if="!iqdCashOnly" class="mx-5 flex">
          <div dir="ltr"> + (c) {{formatMoney(paidInCashUSD, "USD")}} x {{USDToIQDExchangeRate}} = {{formatMoney(Math.round(paidInCashUSD * USDToIQDExchangeRate / 100), "IQD")}}</div>
        </div>
<!--        <div class="mx-5 flex">-->
<!--          <div dir="ltr"> + (d) {{formatMoney(paidInEpaymentUSD, "USD")}} x {{USDToIQDExchangeRate}} = {{formatMoney(paidInEpaymentUSD * USDToIQDExchangeRate, "IQD")}}</div>-->
<!--        </div>-->
        <div v-if="!iqdCashOnly" class="mx-5 flex mt-1">
          <div dir="ltr"> + (d) {{formatMoney(paidInCreditIQD, "IQD")}}</div>
        </div>

        <div class="w-full p-2">
          <hr class="solid" />
        </div>

        <div class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
          <div class="">{{t(usePaidLabel ? "totalPaidLabel" : "totalPaid")}}</div>
          <div dir="ltr" class="bg-green-100 w-1/2 p-1"> {{formatMoney(totalCashAndEpaymentBeforeChange, "IQD")}}</div>
        </div>

        <div class="flex justify-between items-center text-base font-bold mx-5 mt-1" :dir="$ctx.getDir()">
          <div>{{t("changeDue")}}</div>
          <div dir="ltr" class="bg-red-100 w-1/2 p-1"> {{formatMoney(changeDue, "IQD")}}</div>
        </div>

      </div>


      <div id="totals" v-if="defaultCurrency === 'USD'" class="w-full text-sm mt-1" dir="ltr">

        <div class="w-full p-2">
          <hr class="solid" />
        </div>

        <div class="mx-5 flex mt-1">
          <div dir="ltr"> &nbsp &nbsp(a) {{formatMoney(paidInCashUSD, "USD")}}</div>
        </div>
        <div class="mx-5 flex">
          <div dir="ltr"> + (b) {{formatMoney(paidInEpaymentUSD, "USD")}}</div>
        </div>
        <div class="mx-5 flex">
          <div dir="ltr"> + (c) {{formatMoney(paidInCashIQD, "IQD")}} x {{IQDToUSDExchangeRate}} = {{formatMoney(paidInCashIQD * IQDToUSDExchangeRate, "USD")}}</div>
        </div>
<!--        <div class="mx-5 flex">-->
<!--          <div dir="ltr"> + (d) {{formatMoney(paidInEpaymentIQD, "IQD")}} x {{IQDToUSDExchangeRate}} = {{formatMoney(paidInEpaymentIQD * IQDToUSDExchangeRate, "USD")}}</div>-->
<!--        </div>-->

        <div class="w-full p-2">
          <hr class="solid" />
        </div>

        <div class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
          <div class="">{{t(usePaidLabel ? "totalPaidLabel" : "totalPaid")}}</div>
          <div dir="ltr" class="bg-green-100 w-1/2 p-1">{{formatMoney(totalCashAndEpaymentBeforeChange, "USD")}}</div>
        </div>

        <div class="flex justify-between items-center text-base font-bold mx-5 mt-1" :dir="$ctx.getDir()">
          <div>{{t("changeDue")}}</div>
          <div dir="ltr" class="bg-red-100 w-1/2 p-1"> {{formatMoney(changeDue, "USD")}}</div>
        </div>

      </div>
    </div>
  </div>

</template>

<style scoped>
</style>
